import { useState, useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@design-system';
import { Download, X, BarChart3 } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import { SortHeader, renderHeaderCell } from '@/components/DataTableColumn';
import { useSearchParams } from 'react-router-dom';

interface AdvisorsMetricsTableProps {
  getFilteredAdvisors: (filterKey?: string | null) => AdvisorMetrics[];
  isLoading: boolean;
  filterKey?: string | null;
}

const columnHelper = createColumnHelper<AdvisorMetrics>();

export function AdvisorsMetricsTable({
  getFilteredAdvisors,
  isLoading,
  filterKey,
}: AdvisorsMetricsTableProps) {
  const [searchText, setSearchText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const clearFilter = () => {
    setSearchParams({ tab: 'advisors' });
  };

  const checkUsage = () => {
    setSearchParams({ tab: 'usage', usageFilter: filterKey || 'all' });
  };

  const filteredAdvisors = useMemo(() => {
    return getFilteredAdvisors(filterKey);
  }, [filterKey, getFilteredAdvisors]);

  const exportToCsv = () => {
    const headers = [
      'Name',
      'Email',
      'Organization',
      'First Activity',
      'Last Activity',
      'Total Logins',
    ];
    const rows = filteredAdvisors.map(advisor => [
      `${advisor.advisorFirstName} ${advisor.advisorLastName}`,
      advisor.advisorEmail,
      advisor.organizationName,
      advisor.firstOpen ? new Date(advisor.firstOpen).toLocaleDateString() : 'N/A',
      advisor.lastOpen ? new Date(advisor.lastOpen).toLocaleDateString() : 'N/A',
      advisor.totalOpens,
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `advisors-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const columnDefinitions = [
    columnHelper.accessor(row => `${row.advisorFirstName} ${row.advisorLastName}`, {
      id: 'name',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Name
          </SortHeader>,
        ),
      cell: info => <span className="font-medium">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Name' },
    }),
    columnHelper.accessor('advisorEmail', {
      id: 'email',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Email
          </SortHeader>,
        ),
      cell: info => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Email' },
    }),
    columnHelper.accessor('organizationName', {
      id: 'organization',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Organization
          </SortHeader>,
        ),
      cell: info => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Organization' },
    }),
    columnHelper.accessor('firstOpen', {
      id: 'firstActivity',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            First Activity
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">{value ? new Date(value).toLocaleDateString() : 'N/A'}</span>
        );
      },
      enableSorting: true,
      meta: { label: 'First Activity' },
    }),
    columnHelper.accessor('lastOpen', {
      id: 'lastActivity',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Last Activity
          </SortHeader>,
        ),
      cell: info => {
        const value = info.getValue();
        return (
          <span className="text-sm">{value ? new Date(value).toLocaleDateString() : 'N/A'}</span>
        );
      },
      enableSorting: true,
      meta: { label: 'Last Activity' },
    }),
    columnHelper.accessor('totalOpens', {
      id: 'totalLogins',
      header: ({ column }) =>
        renderHeaderCell(
          <SortHeader
            column={column}
            variant="ghost"
          >
            Total Logins
          </SortHeader>,
        ),
      cell: info => <span className="font-medium">{info.getValue()}</span>,
      enableSorting: true,
      meta: { label: 'Total Logins' },
    }),
  ];

  return (
    <DataTable
      columns={columnDefinitions as ColumnDef<AdvisorMetrics>[]}
      data={filteredAdvisors}
      tableId="inactive-advisors-table"
      getRowId={row => row.advisorId}
      isLoading={isLoading}
      variant="outlined"
      initialSorting={[{ id: 'lastActivity', desc: true }]}
      pageSize={30}
      globalSearchText={searchText}
      handleSearchTextChange={setSearchText}
      globalSearchFieldKeys={['name', 'email', 'organization']}
      globalSearchPlaceholder="Search name, email, organization..."
      headerExtras={
        <div className="flex gap-2">
          {filterKey && (
            <>
              <Button
                onClick={clearFilter}
                variant="outline"
                size="sm"
              >
                <X className="mr-2 h-4 w-4" />
                Clear Filter
              </Button>
              <Button
                onClick={checkUsage}
                variant="outline"
                size="sm"
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Check Usage
              </Button>
            </>
          )}
          <Button
            onClick={exportToCsv}
            variant="outline"
            size="sm"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      }
    />
  );
}
